// ---------------------------------------------------------------------------
// Prezio Onboard Widget — OptionCard
// ---------------------------------------------------------------------------
// Full-width selectable card for step options.
// Icon → Name → Description → Chevron/Checkmark.
// Keyboard: Enter/Space to select. 56px min-height touch target.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { cn } from '@/utils/cn';
import styles from './OptionCard.module.css';

interface OptionCardProps {
  id: string;
  name: string;
  description?: string;
  badges?: string[];
  isSelected: boolean;
  onClick: (id: string) => void;
  icon?: ReactNode;
  compact?: boolean;
  className?: string;
}

export function OptionCard({ id, name, description, badges, isSelected, onClick, icon, compact, className }: OptionCardProps) {
  const handleClick = useCallback(() => onClick(id), [onClick, id]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(id);
      }
    },
    [onClick, id],
  );

  return (
    <div
      className={cn(styles['card'], isSelected && styles['selected'], compact && styles['compact'], className)}
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {icon && <span className={styles['icon']}>{icon}</span>}

      <div className={styles['content']}>
        <span className={styles['name']}>{name}</span>
        {description && <span className={styles['description']}>{description}</span>}
        {badges && badges.length > 0 && (
          <div className={styles['badges']}>
            {badges.map((b) => (
              <span key={b} className={styles['badge']}>{b}</span>
            ))}
          </div>
        )}
      </div>

      <span className={styles['indicator']} aria-hidden="true">
        {isSelected ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="10" fill="currentColor" />
            <path
              d="M6 10.5L8.5 13L14 7.5"
              stroke="var(--po-color-primary-foreground)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M7.5 5L12.5 10L7.5 15"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
    </div>
  );
}
